/**
 * MessageComposeDialog (now uses Sheet/Drawer pattern)
 *
 * Unified drawer for composing messages with template and provider selection.
 * Supports both email and WhatsApp channels.
 *
 * Email mode:
 * - Selecting an email template (with smart defaults)
 * - Choosing an email provider (Gmail, Outlook, etc.)
 * - Editing subject and body before opening
 *
 * WhatsApp mode:
 * - Same templates, combined into single message
 * - Opens WhatsApp Web/app with prefilled message
 * - User manually presses "Send" in WhatsApp
 *
 * No in-app sending - only opens the selected provider/app with prefilled content.
 *
 * REFACTORED: Nov 2025 - Converted from Dialog to Sheet (drawer) for better UX
 * with larger content. Uses the same pattern as TemplateEditorDrawer.
 */

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Send, Copy, User, Settings, MessageCircle, Phone } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useMessageComposeStore } from '@/stores/messageCompose.store';
import { useEmailTemplatesForContext } from '@/hooks/useEmailTemplates';
import { interpolateTemplate } from '@/services/emailTemplates.service';
import type { EmailTemplate, EmailTemplateVars } from '@/types/emailTemplates';
import type { EmailTemplateContext } from '@/types/emailTemplates';
import {
  EMAIL_PROVIDERS,
  EmailProvider,
  buildEmailLink,
  getCustomerDisplayName,
  buildEmailClosingBlock,
} from '@/utils/email';
import { buildWhatsAppLink, isValidWhatsAppPhone, formatPhoneForDisplay } from '@/utils/whatsapp';
import { useActiveTeamId } from '@/contexts/TeamContext';
import { useTeamSettings } from '@/features/settings/useTeamSettings';
import { useAuth } from '@/contexts/AuthContext';
import { logMessageOpened } from '@/services/emailEvents.service';
import { useCurrentUserProfile } from '@/hooks/useUserProfile';
import { useCompanyProfiles } from '@/hooks/useCompanyProfiles';
import { Link } from 'react-router-dom';

// =============================================================================
// Component
// =============================================================================

export function MessageComposeDialog() {
  const { t } = useTranslation('common');
  const { isOpen, payload, close } = useMessageComposeStore();
  const { user } = useAuth();

  // Determine channel (email or whatsapp)
  const channel = payload?.channel || 'email';
  const isWhatsApp = channel === 'whatsapp';

  // Get default provider and closing text from team settings
  const teamId = useActiveTeamId() || '';
  const { settings } = useTeamSettings(teamId);
  const defaultProvider = settings?.email?.defaultProvider || 'default';
  const closingText = settings?.email?.closingText;
  const garageName = settings?.company?.legalName || 'Ons bedrijf';

  // Get user profile for fullName
  const { data: userProfile, isLoading: isLoadingProfile } = useCurrentUserProfile();

  // Get company profiles for BV name (invoice context)
  const { profiles: companyProfiles } = useCompanyProfiles(teamId);

  // Get templates for current context from Firestore
  const context = payload?.context as EmailTemplateContext | undefined;
  const { data: firestoreTemplates = [], isLoading: isLoadingTemplates } =
    useEmailTemplatesForContext(context || 'customer');

  // Use Firestore templates (or empty array while loading)
  const templates = firestoreTemplates;

  // Local state
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<string>('');
  const [selectedProvider, setSelectedProvider] = React.useState<EmailProvider>(defaultProvider);
  const [subject, setSubject] = React.useState('');
  const [body, setBody] = React.useState('');
  // Track if user has manually edited subject/body
  const [subjectDirty, setSubjectDirty] = React.useState(false);
  const [bodyDirty, setBodyDirty] = React.useState(false);
  // Track if we've initialized the body for this dialog session
  const hasInitializedRef = React.useRef(false);

  // Build template variables for interpolation
  const templateVars: EmailTemplateVars = React.useMemo(() => {
    if (!payload) {
      return {
        'customer.displayName': '',
        'company.name': garageName,
      };
    }
    const customerName = getCustomerDisplayName(payload.customer);
    return {
      // Customer
      'customer.displayName': customerName,
      'customer.firstName': payload.customer.firstName || '',
      'customer.lastName': payload.customer.lastName || '',
      'customer.email': payload.customer.email || '',
      'customer.phone': payload.customer.phone || '',
      // Vehicle
      'vehicle.licensePlate': payload.licensePlate || '',
      // Company
      'company.name': garageName,
      // User
      'user.fullName': userProfile?.fullName || '',
      // Context-specific
      'invoice.number': payload.invoiceNumber || '',
      'workOrder.id': payload.workOrderId || '',
      'appointment.date': payload.appointmentDate || '',
      'pdf.downloadUrl': payload.pdfDownloadUrl || '',
    };
  }, [payload, garageName, userProfile?.fullName]);

  // Get company name for closing block
  // For invoice context: use the selected company profile
  // For other contexts: use the default company profile or garageName
  const companyNameForClosing = React.useMemo(() => {
    if (!payload) return garageName;

    // For invoice context, try to get the company profile name
    if (context === 'invoice' && payload.companyProfileId && companyProfiles.length > 0) {
      const profile = companyProfiles.find((p) => p.id === payload.companyProfileId);
      if (profile?.name) return profile.name;
    }

    // Fallback to default profile or garageName
    const defaultProfile = companyProfiles.find((p) => p.isDefault) || companyProfiles[0];
    return defaultProfile?.name || garageName;
  }, [payload, context, companyProfiles, garageName]);

  // Build the closing block
  const buildBodyWithClosing = React.useCallback(
    (templateBody: string) => {
      const closing = buildEmailClosingBlock({
        closingText,
        userFullName: userProfile?.fullName,
        companyName: companyNameForClosing,
      });
      return `${templateBody}\n\n${closing}`;
    },
    [closingText, userProfile?.fullName, companyNameForClosing]
  );

  // Reset initialization flag when dialog closes
  React.useEffect(() => {
    if (!isOpen) {
      hasInitializedRef.current = false;
    }
  }, [isOpen]);

  // Helper to resolve template content
  const resolveTemplateContent = React.useCallback(
    (template: EmailTemplate) => {
      const resolvedSubject = interpolateTemplate(template.subjectTemplate, templateVars);
      const resolvedBody = interpolateTemplate(template.bodyTemplate, templateVars);
      return { subject: resolvedSubject, body: buildBodyWithClosing(resolvedBody) };
    },
    [templateVars, buildBodyWithClosing]
  );

  // Initialize state when dialog opens
  // Wait for profile and templates to load before setting body
  React.useEffect(() => {
    // Only initialize once per dialog session, and wait for profile and templates to load
    if (
      isOpen &&
      payload &&
      context &&
      !isLoadingProfile &&
      !isLoadingTemplates &&
      !hasInitializedRef.current &&
      templates.length > 0
    ) {
      hasInitializedRef.current = true;

      // Find default template or use first one
      const defaultTemplate = templates.find((t) => t.isDefault) || templates[0];

      // Check if there's a smart default based on status
      let smartTemplate = defaultTemplate;
      if (payload.invoiceStatus === 'overdue') {
        const reminder = templates.find((t) => t.id === 'invoice-reminder');
        if (reminder) smartTemplate = reminder;
      } else if (payload.workOrderStatus === 'DONE') {
        const ready = templates.find((t) => t.id === 'workorder-ready');
        if (ready) smartTemplate = ready;
      }

      if (smartTemplate) {
        setSelectedTemplateId(smartTemplate.id);
        const { subject: resolvedSubject, body: resolvedBody } =
          resolveTemplateContent(smartTemplate);
        setSubject(resolvedSubject);
        setBody(resolvedBody);
      } else {
        setSelectedTemplateId('');
        setSubject('');
        setBody('');
      }

      // Reset dirty state
      setSubjectDirty(false);
      setBodyDirty(false);

      // Set default provider
      setSelectedProvider(defaultProvider);
    }
  }, [
    isOpen,
    payload,
    context,
    defaultProvider,
    templates,
    resolveTemplateContent,
    isLoadingProfile,
    isLoadingTemplates,
  ]);

  // Handle template change
  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = templates.find((t: EmailTemplate) => t.id === templateId);
    if (template) {
      // Show toast if user has edited content
      if (subjectDirty || bodyDirty) {
        toast.info('Template toegepast', {
          description: 'Onderwerp en bericht zijn vervangen.',
        });
      }
      const { subject: resolvedSubject, body: resolvedBody } = resolveTemplateContent(template);
      setSubject(resolvedSubject);
      setBody(resolvedBody);
      // Reset dirty state after template change
      setSubjectDirty(false);
      setBodyDirty(false);
    }
  };

  // Handle subject change
  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSubject(e.target.value);
    setSubjectDirty(true);
  };

  // Handle body change
  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBody(e.target.value);
    setBodyDirty(true);
  };

  // Copy body to clipboard
  const handleCopyBody = () => {
    navigator.clipboard
      .writeText(body)
      .then(() => toast.success(t('toasts.clipboard.messageCopied')))
      .catch(() => toast.error(t('toasts.clipboard.copyFailed')));
  };

  // Handle open email
  const handleOpenEmail = () => {
    if (!payload?.customer.email || !context) return;

    const url = buildEmailLink({
      provider: selectedProvider,
      to: payload.customer.email,
      subject,
      body,
    });

    // Log the email event (fire and forget - don't block the UI)
    if (teamId) {
      logMessageOpened(teamId, {
        channel: 'email',
        context,
        templateId: selectedTemplateId,
        provider: selectedProvider,
        recipient: payload.customer.email,
        subject,
        invoiceNumber: payload.invoiceNumber,
        workOrderId: payload.workOrderId,
        createdBy: user?.uid || 'unknown',
      }).catch(() => {
        // Silently ignore logging errors
      });
    }

    // Open in new tab for web providers, same tab for mailto
    if (selectedProvider === 'default') {
      window.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }

    // Call onSend callback if provided (e.g., to mark reminder as handled)
    payload?.onSend?.();

    close();
  };

  // Handle open WhatsApp
  const handleOpenWhatsApp = () => {
    if (!context) return;

    const phone = payload?.customer.phone;

    // Validate phone number
    if (!isValidWhatsAppPhone(phone)) {
      toast.error(t('messaging.noValidWhatsApp'), {
        description: t('messaging.noValidWhatsAppDescription'),
      });
      return;
    }

    // Build WhatsApp message: subject + body (body already includes closing)
    // For WhatsApp, we combine subject and body into a single message
    const whatsappMessage = subject ? `*${subject}*\n\n${body}`.trim() : body.trim();

    const url = buildWhatsAppLink({
      phone: phone!,
      message: whatsappMessage,
    });

    // Log the WhatsApp event (fire and forget - don't block the UI)
    if (teamId) {
      logMessageOpened(teamId, {
        channel: 'whatsapp',
        context,
        templateId: selectedTemplateId,
        recipient: phone!,
        subject,
        invoiceNumber: payload?.invoiceNumber,
        workOrderId: payload?.workOrderId,
        createdBy: user?.uid || 'unknown',
      }).catch(() => {
        // Silently ignore logging errors
      });
    }

    // Always open in new tab
    window.open(url, '_blank', 'noopener,noreferrer');

    // Call onSend callback if provided (e.g., to mark reminder as handled)
    payload?.onSend?.();

    close();
  };

  // Handle send action (email or WhatsApp based on channel)
  const handleSend = () => {
    if (isWhatsApp) {
      handleOpenWhatsApp();
    } else {
      handleOpenEmail();
    }
  };

  // Handle keyboard shortcut (Cmd/Ctrl + Enter to send)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  // Don't render if no payload
  if (!payload) return null;

  const customerEmail = payload.customer.email || '';
  const customerPhone = payload.customer.phone || '';
  const customerName = getCustomerDisplayName(payload.customer);
  const hasEmail = Boolean(customerEmail);
  const hasValidPhone = isValidWhatsAppPhone(customerPhone);

  // Determine if send button should be enabled
  const canSend = isWhatsApp ? hasValidPhone : hasEmail;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && close()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg flex flex-col p-0"
        onKeyDown={handleKeyDown}
      >
        {/* Header - fixed at top */}
        <SheetHeader className="px-4 py-3 border-b flex-shrink-0">
          <SheetTitle className="flex items-center gap-2 text-base">
            {isWhatsApp ? (
              <MessageCircle className="h-4 w-4 text-green-600" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
            {isWhatsApp ? 'WhatsApp bericht' : 'E-mail opstellen'}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {isWhatsApp
              ? `Stuur een bericht naar ${customerName}`
              : `Stuur een e-mail naar ${customerName}`}
          </SheetDescription>
        </SheetHeader>

        {/* Scrollable content area */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="px-4 py-3">
            {/* Loading state while profile loads */}
            {isLoadingProfile || isLoadingTemplates ? (
              <div className="space-y-3">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : (
              <div className="space-y-3">
                {/* Section: Recipient - compact inline */}
                <div className="flex items-center gap-2 py-1.5 px-2 rounded-md bg-muted/40">
                  {isWhatsApp ? (
                    <Phone className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <User className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium">{customerName}</span>
                    <span className="text-muted-foreground mx-1.5">—</span>
                    <span className="text-sm text-muted-foreground">
                      {isWhatsApp ? (
                        hasValidPhone ? (
                          formatPhoneForDisplay(customerPhone)
                        ) : (
                          <span className="text-destructive text-xs">
                            {t('messaging.noValidPhone')}
                          </span>
                        )
                      ) : (
                        customerEmail
                      )}
                    </span>
                  </div>
                </div>

                {/* Section: Template Selection - dropdown */}
                {templates.length > 0 && (
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-muted-foreground">Template</Label>
                    <Select value={selectedTemplateId} onValueChange={handleTemplateChange}>
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Kies een template" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Section: Message - compact */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground">Bericht</Label>

                  {/* Subject - hidden for WhatsApp, shown for email */}
                  {!isWhatsApp && (
                    <div className="space-y-1">
                      <Label htmlFor="email-subject" className="text-xs text-muted-foreground">
                        Onderwerp
                      </Label>
                      <Input
                        id="email-subject"
                        value={subject}
                        onChange={handleSubjectChange}
                        placeholder="Onderwerp"
                        className="h-8 text-sm"
                      />
                    </div>
                  )}

                  {/* Body */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="email-body" className="text-xs text-muted-foreground">
                        {isWhatsApp ? 'Bericht' : 'Inhoud'}
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-5 px-1.5 text-xs"
                        onClick={handleCopyBody}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Kopieer
                      </Button>
                    </div>
                    <Textarea
                      id="email-body"
                      value={body}
                      onChange={handleBodyChange}
                      placeholder={isWhatsApp ? 'Typ hier uw bericht...' : 'Typ hier uw bericht...'}
                      rows={isWhatsApp ? 8 : 6}
                      className="resize-none text-sm min-h-[140px] p-2"
                    />
                    {isWhatsApp && subject && (
                      <p className="text-xs text-muted-foreground">
                        Onderwerp "{subject}" wordt als titel toegevoegd.
                      </p>
                    )}
                  </div>
                </div>

                {/* Section: Provider Selection - compact */}
                {!isWhatsApp && (
                  <div className="space-y-1.5 pt-1">
                    <div className="flex items-center gap-1.5">
                      <Settings className="h-3 w-3 text-muted-foreground" />
                      <Label className="text-xs font-medium text-muted-foreground">
                        E-mailprogramma
                      </Label>
                    </div>
                    <Select
                      value={selectedProvider}
                      onValueChange={(value) => setSelectedProvider(value as EmailProvider)}
                    >
                      <SelectTrigger id="email-provider" className="h-8 text-sm">
                        <SelectValue placeholder="Kies programma" />
                      </SelectTrigger>
                      <SelectContent>
                        {EMAIL_PROVIDERS.map((provider) => (
                          <SelectItem key={provider.value} value={provider.value}>
                            {provider.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Wijzig in{' '}
                      <Link
                        to="/settings?tab=preferences"
                        className="underline hover:text-foreground"
                        onClick={close}
                      >
                        Instellingen
                      </Link>
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer - sticky at bottom, compact */}
        <SheetFooter className="flex-shrink-0 px-4 py-2 border-t bg-background">
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 w-full">
            <Button variant="outline" size="sm" onClick={close}>
              {t('actions.cancel')}
            </Button>
            <Button
              size="sm"
              onClick={handleSend}
              disabled={!canSend}
              className={isWhatsApp ? 'bg-green-600 hover:bg-green-700' : undefined}
            >
              {isWhatsApp ? (
                <>
                  <MessageCircle className="mr-1.5 h-3.5 w-3.5" />
                  {t('messaging.openWhatsApp')}
                </>
              ) : (
                <>
                  <Send className="mr-1.5 h-3.5 w-3.5" />
                  {t('messaging.openEmail')}
                </>
              )}
              <span className="ml-1.5 text-xs opacity-70 hidden sm:inline">⌘↵</span>
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
